import React, { useEffect, useState } from 'react';

const badgeStyles: Record<string, React.CSSProperties> = {
  green: { background: '#00C9A720', color: '#00C9A7' },
  purple: { background: '#845EC220', color: '#845EC2' },
  red: { background: '#FF6B6B20', color: '#FF6B6B' },
  yellow: { background: '#F9A82520', color: '#F9A825' },
  trial: { background: '#FF6B6B15', color: '#FF6B6B' },
  starter: { background: '#00C9A715', color: '#00C9A7' },
  growth: { background: '#845EC215', color: '#845EC2' },
  enterprise: { background: '#F9A82515', color: '#F9A825' },
  blue: { background: '#3B82F620', color: '#3B82F6' },
};

export function Badge({ type, children }: { type: string; children: React.ReactNode }) {
  const style = badgeStyles[type] || badgeStyles.green;
  return <span style={{ ...style, display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>{children}</span>;
}

export function StatCard({ label, value, delta, color }: { label: string; value: string | number; delta?: string; color: string }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 20, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color }} />
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, fontFamily: 'var(--mono)', color }}>{value}</div>
      {delta && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>{delta}</div>}
    </div>
  );
}

export function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', marginBottom: 24, ...style }}>{children}</div>;
}

export function CardHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      {action}
    </div>
  );
}

export function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{headers.map(h => <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, mono, style }: { children: React.ReactNode; mono?: boolean; style?: React.CSSProperties }) {
  return <td style={{ padding: '13px 16px', fontSize: 13, borderBottom: '1px solid var(--border)', fontFamily: mono ? 'var(--mono)' : undefined, ...style }}>{children}</td>;
}

export function EmptyRow({ cols, message = 'No data yet' }: { cols: number; message?: string }) {
  return <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 14 }}>{message}</td></tr>;
}

type BtnVariant = 'accent' | 'outline' | 'danger' | 'warn';
export function Btn({ children, onClick, variant = 'accent', size = 'md', disabled, style, type }: {
  children: React.ReactNode; onClick?: () => void; variant?: BtnVariant;
  size?: 'sm' | 'md'; disabled?: boolean; style?: React.CSSProperties; type?: 'button' | 'submit';
}) {
  const base: React.CSSProperties = { border: 'none', borderRadius: size === 'sm' ? 6 : 8, padding: size === 'sm' ? '6px 12px' : '9px 16px', fontSize: size === 'sm' ? 12 : 13, fontWeight: 700, transition: 'all 0.15s', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 };
  const variants: Record<BtnVariant, React.CSSProperties> = {
    accent: { background: 'var(--accent)', color: '#000' },
    outline: { background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' },
    danger: { background: 'transparent', border: '1px solid var(--border)', color: 'var(--danger)' },
    warn: { background: '#F9A82520', border: '1px solid #F9A82540', color: 'var(--warn)' },
  };
  return <button type={type || 'button'} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>{children}</button>;
}

export function Modal({ title, onClose, children, width }: { title: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, width: '100%', maxWidth: width || 520, maxHeight: '90vh', overflowY: 'auto', animation: 'fadeUp 0.25s ease' }}>
        <div style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer', padding: 4 }}>×</button>
        </div>
        <div style={{ padding: '0 24px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

export function FormField({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{hint}</div>}
    </div>
  );
}

const inputStyle: React.CSSProperties = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none', width: '100%' };

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...inputStyle, ...props.style }} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} style={{ ...inputStyle, ...props.style }}>{props.children}</select>;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', ...props.style }} />;
}

type Toast = { id: number; message: string; type: 'success' | 'error' };
let toastFn: ((msg: string, type?: 'success' | 'error') => void) | null = null;

export function useToast() {
  return (msg: string, type: 'success' | 'error' = 'success') => toastFn?.(msg, type);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => {
    toastFn = (message, type = 'success') => {
      const id = Date.now();
      setToasts(t => [...t, { id, message, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
    };
  }, []);
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderLeft: `3px solid ${t.type === 'success' ? 'var(--accent)' : 'var(--danger)'}`, borderRadius: 10, padding: '12px 16px', fontSize: 13, minWidth: 260, display: 'flex', alignItems: 'center', gap: 10, animation: 'slideIn 0.3s ease' }}>
          {t.type === 'success' ? '✅' : '❌'} {t.message}
        </div>
      ))}
    </div>
  );
}

export function CompanyInit({ name, size = 36 }: { name: string; size?: number }) {
  return <div style={{ width: size, height: size, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #845EC2, #00C9A7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.4, color: '#fff' }}>{(name || '?').charAt(0).toUpperCase()}</div>;
}

export function SearchBar({ placeholder, onSearch }: { placeholder: string; onSearch: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 14px', marginBottom: 20 }}>
      <span>🔍</span>
      <input type="text" placeholder={placeholder} onChange={e => onSearch(e.target.value)} style={{ flex: 1, background: 'none', border: 'none', outline: 'none', padding: '12px 0', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font)' }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px' }}>{title}</div>
        {subtitle && <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

export function FeatureLocked({ feature }: { feature: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{feature} Not Enabled</div>
      <div style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 400, margin: '0 auto' }}>
        This feature is not enabled for your company. Contact your administrator to upgrade your plan.
      </div>
    </div>
  );
}
